import { useState, useEffect } from 'react';
import { getPatients } from '../../services/api';
import api from '../../services/api';

const TYPE_OPTIONS = [
    { value: 'standard',       icon: 'ti-box',     label: 'Standard' },
    { value: 'recup',          icon: 'ti-refresh', label: 'Récupération' },
    { value: 'installation',   icon: 'ti-tool',    label: 'Installation' },
    { value: 'envoi_tnt',      icon: 'ti-truck',   label: 'Envoi TNT' },
    { value: 'envoi_postal',   icon: 'ti-mail',    label: 'Envoi Postal' },
    { value: 'visite_patient', icon: 'ti-home',    label: 'Visite patient' },
];

const PATHOLOGIE_COLORS = {
    nutrition: '#16a34a',
    perfusion: '#2563eb',
    parkinson: '#1a1a2e',
};

export default function DeliveryForm({ onSave, onCancel, initialDate, initialPeriode }) {
    const [patients, setPatients] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isRecurrent, setIsRecurrent] = useState(false);
    const [form, setForm] = useState({
        patient: '',
        livreur: '',
        date: initialDate || '',
        periode: initialPeriode || 'matin',
        consigneHoraire: '',
        typelivraison: 'standard',
        notes: '',
        position: 0,
        imuno: false,
        adresseSecondaireChoisie: false,
    });
    const [recurrenceForm, setRecurrenceForm] = useState({
        jourSemaine: 'lundi',
        frequence: '1',
        dateFin: '',
    });
    const [secteurs, setSecteurs] = useState([]);
    const [showNewPatient, setShowNewPatient] = useState(false);
    const [newPatient, setNewPatient] = useState({
        nom: '', ville: '', adresse: '',
        pathologie: '', statutPatient: 'actif', secteur: ''
    });
    const [creatingPatient, setCreatingPatient] = useState(false);
    const [patientSearch, setPatientSearch] = useState('');
    const [showSuggestions, setShowSuggestions] = useState(false);

    useEffect(() => {
        const loadData = async () => {
            try {
                const [patientsRes, usersRes, secteursRes] = await Promise.all([
                    getPatients(),
                    api.get('/users'),
                    api.get('/secteurs'),
                ]);
                setPatients(patientsRes.data.member || []);
                setUsers(usersRes.data.member || []);
                setSecteurs(secteursRes.data.member || []);
            } catch (err) {
                console.error(err);
            }
        };
        loadData();
    }, []);

    const handleChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            let recurrenceIri = null;

            if (isRecurrent) {
                const recurrenceRes = await api.post('/recurrences', {
                    patient: form.patient,
                    livreur: form.livreur,
                    jourSemaine: recurrenceForm.jourSemaine,
                    periode: form.periode,
                    consigneHoraire: form.consigneHoraire || null,
                    dateDebut: form.date,
                    dateFin: recurrenceForm.dateFin || null,
                    frequence: recurrenceForm.frequence === '30j' ? 99 : parseInt(recurrenceForm.frequence),
                    actif: true,
                }, { headers: { 'Content-Type': 'application/ld+json' } });
                recurrenceIri = recurrenceRes.data['@id'];
            }

            await api.post('/deliveries', {
                patient: form.patient,
                livreur: form.livreur,
                date: form.date,
                periode: form.periode,
                consigneHoraire: form.consigneHoraire || null,
                statut: 'planifié',
                statutPrep: 'a_contacter',
                typelivraison: form.typelivraison || 'standard',
                position: 0,
                notes: form.notes,
                recurrence: recurrenceIri,
                imuno: form.imuno,
                adresseSecondaireChoisie: form.adresseSecondaireChoisie,
            }, { headers: { 'Content-Type': 'application/ld+json' } });

            onSave();
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleCreatePatient = async () => {
        if (!newPatient.nom || !newPatient.ville || !newPatient.adresse) {
            alert('Nom, ville et adresse sont obligatoires');
            return;
        }
        setCreatingPatient(true);
        try {
            const res = await api.post('/patients', {
                ...newPatient,
                actif: true,
            }, { headers: { 'Content-Type': 'application/ld+json' } });

            const patientsRes = await getPatients();
            setPatients(patientsRes.data.member || []);
            setForm({ ...form, patient: res.data['@id'] });
            setPatientSearch(`${res.data.nom} — ${res.data.ville}`);
            setShowNewPatient(false);
            setNewPatient({ nom: '', ville: '', adresse: '', pathologie: '', statutPatient: 'actif' });
        } catch (err) {
            console.error(err);
        } finally {
            setCreatingPatient(false);
        }
    };

    const selectedPatient = patients.find(p => p['@id'] === form.patient);

    return (
        <form onSubmit={handleSubmit}>
            {/* Section : Patient */}
            <div style={sectionStyle}>
                <div style={fieldStyle}>
                    <label style={labelStyle}>Patient *</label>
                    <div style={{ display: 'flex', gap: '8px' }}>
                        <div style={{ flex: 1, position: 'relative' }}>
                            <input
                                type="text"
                                placeholder="Rechercher un patient..."
                                value={patientSearch}
                                onChange={e => {
                                    setPatientSearch(e.target.value);
                                    setShowSuggestions(true);
                                    if (!e.target.value) {
                                        setForm({ ...form, patient: '' });
                                    }
                                }}
                                onFocus={() => setShowSuggestions(true)}
                                onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                                style={{ ...inputStyle, borderColor: form.patient ? '#4f46e5' : '#ddd' }}
                                required={!form.patient}
                            />
                            {form.patient && (
                                <span style={{
                                    position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)',
                                    color: '#4f46e5', fontSize: '15px',
                                }}>✓</span>
                            )}
                            {showSuggestions && patientSearch.length > 0 && (
                                <div style={{
                                    position: 'absolute', top: '100%', left: 0, right: 0,
                                    backgroundColor: 'white', border: '1px solid #ddd', borderRadius: '8px',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 100,
                                    maxHeight: '180px', overflowY: 'auto',
                                }}>
                                    {patients
                                        .filter(p => p.actif !== false)
                                        .filter(p =>
                                            p.nom.toLowerCase().includes(patientSearch.toLowerCase()) ||
                                            p.ville?.toLowerCase().includes(patientSearch.toLowerCase())
                                        )
                                        .slice(0, 8)
                                        .map(p => (
                                            <div
                                                key={p['@id']}
                                                onMouseDown={() => {
                                                    setForm({ ...form, patient: p['@id'] });
                                                    setPatientSearch(`${p.nom} — ${p.ville}`);
                                                    setShowSuggestions(false);
                                                }}
                                                style={{
                                                    padding: '8px 12px', cursor: 'pointer', fontSize: '13px',
                                                    borderBottom: '1px solid #f0f0f0',
                                                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                                }}
                                                onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f5f3ff'}
                                                onMouseLeave={e => e.currentTarget.style.backgroundColor = 'white'}
                                            >
                                                <span style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '500' }}>
                                                    <span style={{
                                                        width: '10px', height: '10px', borderRadius: '50%',
                                                        backgroundColor: PATHOLOGIE_COLORS[p.pathologie] || '#ccc',
                                                        flexShrink: 0,
                                                    }} />{p.nom}
                                                </span>
                                                <span style={{ color: '#888', fontSize: '12px' }}>{p.ville}</span>
                                            </div>
                                        ))
                                    }
                                    {patients.filter(p =>
                                        p.nom.toLowerCase().includes(patientSearch.toLowerCase()) ||
                                        p.ville?.toLowerCase().includes(patientSearch.toLowerCase())
                                    ).length === 0 && (
                                        <div style={{ padding: '8px 12px', fontSize: '13px', color: '#888' }}>
                                            Aucun patient trouvé
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                        <button
                            type="button"
                            onClick={() => setShowNewPatient(!showNewPatient)}
                            style={{
                                padding: '8px 12px',
                                backgroundColor: showNewPatient ? '#fee2e2' : '#ede9fe',
                                color: showNewPatient ? '#dc2626' : '#4f46e5',
                                border: 'none', borderRadius: '8px', cursor: 'pointer',
                                fontSize: '13px', fontWeight: '600', whiteSpace: 'nowrap',
                            }}
                        >
                            {showNewPatient ? '✕ Annuler' : '+ Nouveau'}
                        </button>
                    </div>
                </div>

                {selectedPatient?.adresseSecondaire && (
                    <div style={fieldStyle}>
                        <label style={labelStyle}>📍 Adresse de livraison</label>
                        <div style={{ display: 'flex', gap: '6px' }}>
                            <button
                                type="button"
                                onClick={() => setForm({ ...form, adresseSecondaireChoisie: false })}
                                style={{
                                    flex: 1, padding: '8px 10px', borderRadius: '6px',
                                    border: `1.5px solid ${!form.adresseSecondaireChoisie ? 'var(--accent-color)' : '#e0e0e0'}`,
                                    backgroundColor: !form.adresseSecondaireChoisie ? '#ede9fe' : 'white',
                                    color: !form.adresseSecondaireChoisie ? 'var(--accent-color)' : '#888',
                                    fontSize: '12px', fontWeight: !form.adresseSecondaireChoisie ? '700' : '500',
                                    cursor: 'pointer', textAlign: 'left',
                                }}
                            >
                                🏠 Principale
                                <div style={{ fontSize: '10px', fontWeight: '400', marginTop: '2px', opacity: 0.8 }}>
                                    {selectedPatient.adresse}, {selectedPatient.ville}
                                </div>
                            </button>
                            <button
                                type="button"
                                onClick={() => setForm({ ...form, adresseSecondaireChoisie: true })}
                                style={{
                                    flex: 1, padding: '8px 10px', borderRadius: '6px',
                                    border: `1.5px solid ${form.adresseSecondaireChoisie ? 'var(--accent-color)' : '#e0e0e0'}`,
                                    backgroundColor: form.adresseSecondaireChoisie ? '#ede9fe' : 'white',
                                    color: form.adresseSecondaireChoisie ? 'var(--accent-color)' : '#888',
                                    fontSize: '12px', fontWeight: form.adresseSecondaireChoisie ? '700' : '500',
                                    cursor: 'pointer', textAlign: 'left',
                                }}
                            >
                                🏡 Secondaire
                                <div style={{ fontSize: '10px', fontWeight: '400', marginTop: '2px', opacity: 0.8 }}>
                                    {selectedPatient.adresseSecondaire}{selectedPatient.villeSecondaire ? `, ${selectedPatient.villeSecondaire}` : ''}
                                </div>
                            </button>
                        </div>
                    </div>
                )}

                {showNewPatient && (
                    <div style={{
                        backgroundColor: '#f8fafc', borderRadius: '8px', padding: '12px',
                        marginTop: '12px', border: '1px solid #e0e0e0',
                    }}>
                        <div style={{ fontSize: '12px', fontWeight: '700', color: '#444', marginBottom: '10px' }}>
                            🏥 Nouveau patient
                        </div>
                        <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                            <input
                                placeholder="Nom *"
                                value={newPatient.nom}
                                onChange={e => setNewPatient({ ...newPatient, nom: e.target.value })}
                                style={{ ...inputStyle, flex: 1 }}
                                required
                            />
                            <input
                                placeholder="Ville *"
                                value={newPatient.ville}
                                onChange={e => setNewPatient({ ...newPatient, ville: e.target.value })}
                                style={{ ...inputStyle, flex: 1 }}
                                required
                            />
                        </div>
                        <div style={{ marginBottom: '8px' }}>
                            <input
                                placeholder="Adresse *"
                                value={newPatient.adresse}
                                onChange={e => setNewPatient({ ...newPatient, adresse: e.target.value })}
                                style={inputStyle}
                                required
                            />
                        </div>
                        <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                            <select
                                value={newPatient.pathologie}
                                onChange={e => setNewPatient({ ...newPatient, pathologie: e.target.value })}
                                style={{ ...inputStyle, flex: 1 }}
                            >
                                <option value="">— Pathologie —</option>
                                <option value="nutrition">🟢 Nutrition</option>
                                <option value="perfusion">🔵 Perfusion</option>
                                <option value="parkinson">⚫ Parkinson</option>
                            </select>
                            <select
                                value={newPatient.statutPatient}
                                onChange={e => setNewPatient({ ...newPatient, statutPatient: e.target.value })}
                                style={{ ...inputStyle, flex: 1 }}
                            >
                                <option value="actif">✅ Actif</option>
                                <option value="en_pause">⏸️ En pause</option>
                                <option value="ftt">🔴 FTT</option>
                            </select>
                            <select
                                value={newPatient.secteur}
                                onChange={e => setNewPatient({ ...newPatient, secteur: e.target.value })}
                                style={{ ...inputStyle, flex: 1 }}
                            >
                                <option value="">— Secteur —</option>
                                {secteurs.map(s => (
                                    <option key={s['@id']} value={s['@id']}>{s.nom}</option>
                                ))}
                            </select>
                        </div>
                        <button
                            type="button"
                            onClick={handleCreatePatient}
                            disabled={creatingPatient}
                            style={{
                                width: '100%', padding: '8px', backgroundColor: '#4f46e5', color: 'white',
                                border: 'none', borderRadius: '8px', cursor: 'pointer',
                                fontSize: '13px', fontWeight: '600',
                            }}
                        >
                            {creatingPatient ? 'Création...' : 'Créer et sélectionner'}
                        </button>
                    </div>
                )}
            </div>

            {/* Section : Détails livraison */}
            <div style={sectionStyle}>
                <div style={fieldStyle}>
                    <label style={labelStyle}>Livreur *</label>
                    <select name="livreur" value={form.livreur} onChange={handleChange} style={inputStyle} required>
                        <option value="">Sélectionner un livreur</option>
                        {users.map(u => (
                            <option key={u['@id']} value={u['@id']}>{u.nom || u.email}</option>
                        ))}
                    </select>
                </div>

                <div style={{display: 'flex', gap: '12px'}}>
                    <div style={{...fieldStyle, flex: 1}}>
                        <label style={labelStyle}>Date *</label>
                        <input type="date" name="date" value={form.date} onChange={handleChange} style={inputStyle}
                               required/>
                    </div>
                    <div style={{...fieldStyle, flex: 1}}>
                        <label style={labelStyle}>Période *</label>
                        <select name="periode" value={form.periode} onChange={handleChange} style={inputStyle}>
                            <option value="matin">Matin</option>
                            <option value="aprem">Après-midi</option>
                        </select>
                    </div>
                    <div style={{...fieldStyle, flex: 1.4}}>
                        <label style={labelStyle}>⏰ Consigne horaire</label>
                        <input
                            type="text"
                            name="consigneHoraire"
                            value={form.consigneHoraire}
                            onChange={handleChange}
                            style={inputStyle}
                            placeholder="Ex: avant 10h..."
                        />
                    </div>
                </div>

                <div style={{...fieldStyle, marginBottom: 0}}>
                    <label style={labelStyle}>Type de livraison</label>
                    <div style={{display: 'flex', gap: '6px'}}>
                        {TYPE_OPTIONS.map(opt => {
                            const active = form.typelivraison === opt.value;
                            return (
                                <button
                                    key={opt.value}
                                    type="button"
                                    title={opt.label}
                                    onClick={() => setForm({...form, typelivraison: opt.value})}
                                    style={{
                                        flex: 1, height: '38px', padding: 0,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        borderRadius: '8px',
                                        border: `1.5px solid ${active ? 'var(--accent-color)' : '#e0e0e0'}`,
                                        backgroundColor: active ? '#ede9fe' : 'white',
                                        color: active ? 'var(--accent-color)' : '#888',
                                        fontSize: '16px',
                                        cursor: 'pointer',
                                    }}
                                >
                                    <i className={`ti ${opt.icon}`} aria-hidden="true"></i>
                                </button>
                            );
                        })}
                        <button
                            type="button"
                            title="Livraison avec immuno"
                            onClick={() => setForm({...form, imuno: !form.imuno})}
                            style={{
                                flex: 1, height: '38px', padding: 0,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                borderRadius: '8px',
                                border: '1.5px solid #f59e0b',
                                backgroundColor: form.imuno ? '#fef3c7' : 'white',
                                color: form.imuno ? '#d97706' : '#888',
                                fontSize: '16px',
                                cursor: 'pointer',
                            }}
                        >
                            <i className="ti ti-star" aria-hidden="true"></i>
                        </button>
                    </div>
                </div>
            </div>

            {/* Section : Options */}
            <div style={sectionStyle}>
                <div style={fieldStyle}>
                    <label style={labelStyle}>Notes</label>
                    <textarea
                        name="notes"
                        value={form.notes}
                        onChange={handleChange}
                        style={{...inputStyle, height: '48px', resize: 'vertical'}}
                    />
                </div>

                <div style={{
                    backgroundColor: 'white',
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    padding: '10px 12px'
                }}>
                    <label style={{
                        display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer',
                        marginBottom: isRecurrent ? '10px' : 0,
                    }}>
                        <input
                            type="checkbox"
                            checked={isRecurrent}
                            onChange={e => setIsRecurrent(e.target.checked)}
                        />
                        <span style={{fontSize: '12px', fontWeight: '600', color: '#444'}}>
                            🔄 Livraison récurrente
                        </span>
                    </label>

                    {isRecurrent && (
                        <div style={{display: 'flex', gap: '12px'}}>
                            <div style={{...fieldStyle, flex: 1, marginBottom: 0}}>
                                <label style={labelStyle}>Jour</label>
                                <select
                                    value={recurrenceForm.jourSemaine}
                                    onChange={e => setRecurrenceForm({...recurrenceForm, jourSemaine: e.target.value})}
                                    style={inputStyle}
                                >
                                    <option value="lundi">Lundi</option>
                                    <option value="mardi">Mardi</option>
                                    <option value="mercredi">Mercredi</option>
                                    <option value="jeudi">Jeudi</option>
                                    <option value="vendredi">Vendredi</option>
                                </select>
                            </div>
                            <div style={{ ...fieldStyle, flex: 1, marginBottom: 0 }}>
                                <label style={labelStyle}>Fréquence</label>
                                <select
                                    value={recurrenceForm.frequence}
                                    onChange={e => setRecurrenceForm({ ...recurrenceForm, frequence: e.target.value })}
                                    style={inputStyle}
                                >
                                    <option value="1">Chaque semaine</option>
                                    <option value="2">Toutes les 2 sem.</option>
                                    <option value="4">Toutes les 4 sem.</option>
                                    <option value="30j">Tous les 30j</option>
                                </select>
                            </div>
                            <div style={{ ...fieldStyle, flex: 1, marginBottom: 0 }}>
                                <label style={labelStyle}>Fin (optionnel)</label>
                                <input
                                    type="date"
                                    value={recurrenceForm.dateFin}
                                    onChange={e => setRecurrenceForm({ ...recurrenceForm, dateFin: e.target.value })}
                                    style={inputStyle}
                                />
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Boutons */}
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button type="button" onClick={onCancel} style={cancelButtonStyle}>Annuler</button>
                <button type="submit" style={submitButtonStyle} disabled={loading}>
                    {loading ? 'Enregistrement...' : 'Créer la livraison'}
                </button>
            </div>
        </form>
    );
}

const sectionStyle = {
    borderLeft: '3px solid #4f46e5',
    paddingLeft: '14px',
    marginBottom: '16px',
};
const fieldStyle = { marginBottom: '12px' };
const labelStyle = { display: 'block', marginBottom: '5px', fontSize: '12px', fontWeight: '600', color: '#444' };
const inputStyle = {
    width: '100%',
    padding: '8px 11px',
    borderRadius: '8px',
    border: '1px solid #ddd',
    fontSize: '13px',
    boxSizing: 'border-box',
};
const cancelButtonStyle = {
    padding: '9px 18px',
    backgroundColor: '#f0f2f5',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '13px',
};
const submitButtonStyle = {
    padding: '9px 18px',
    backgroundColor: '#4f46e5',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '600',
};